import * as i0 from '@angular/core';
import { Directive, TemplateRef, Component, Input, ViewChild, ContentChild, EventEmitter, Output, ContentChildren, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { isObservable, of } from 'rxjs';
import { take, filter } from 'rxjs/operators';

/**
 * Use this directive for tab content that you want to lazy load only when that tab is active
 * Usage: `<ps-ui-tab><ng-template psUiTabContent><some-component></some-component></ng-template></ps-ui-tab-content>`
 */
class TabContentDirective {
    constructor(template) {
        this.template = template;
    }
}
TabContentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TabContentDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
TabContentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: TabContentDirective, selector: "[psUiTabContent]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TabContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psUiTabContent]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

/**
 * A single tab element and it's content. This is used alongside the `TabGroupComponent` for displaying the active tab.
 * To have your content lazy loaded only when the tab is active wrap the tab content with `<ng-template>` tags and
 * use the `TabContentDirective`.
 */
class TabComponent {
    constructor() {
        /** The label to display for this tab */
        this.label = '';
        this.icon = '';
        /** Disable this tab */
        this.disabled = false;
        this.className = '';
        /** Template inside the TabComponent view that contains an `<ng-content>`. */
        this._implicitContent = null;
        /** Lazy loaded template that's wrapped in ng-template tags and using the psUiTabContent directive */
        this._explicitContent = null;
    }
    get content() {
        return this._explicitContent || this._implicitContent;
    }
}
TabComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TabComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TabComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: TabComponent, selector: "ps-ui-tab", inputs: { label: "label", icon: "icon", disabled: "disabled", count: "count", className: "className" }, queries: [{ propertyName: "_explicitContent", first: true, predicate: TabContentDirective, descendants: true, read: TemplateRef, static: true }], viewQueries: [{ propertyName: "_implicitContent", first: true, predicate: TemplateRef, descendants: true, static: true }], ngImport: i0, template: "<ng-template><ng-content></ng-content></ng-template>\n", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-tab', template: "<ng-template><ng-content></ng-content></ng-template>\n", styles: [""] }]
        }], ctorParameters: function () { return []; }, propDecorators: { label: [{
                type: Input
            }], icon: [{
                type: Input
            }], disabled: [{
                type: Input
            }], count: [{
                type: Input
            }], className: [{
                type: Input
            }], _implicitContent: [{
                type: ViewChild,
                args: [TemplateRef, { static: true }]
            }], _explicitContent: [{
                type: ContentChild,
                args: [TabContentDirective, { read: TemplateRef, static: true }]
            }] } });

/**
 * A wrapper component around one or more `TabComponent`s. This handles selecting the active tab and displaying it's content
 */
class TabGroupComponent {
    constructor() {
        this._activeIndex = 0;
        /** Emits the selected tabs index when it is selected */
        this.activeIndexChange = new EventEmitter();
        /** Optional callback function that can prevent changing tabs if it returns false */
        this.beforeTabChange = (currentIndex, nextIndex) => true;
        this.tabAlignment = 'center';
        this.activeTabContent = null;
    }
    /** Set the active tab to the specific index */
    get activeIndex() {
        return this._activeIndex;
    }
    set activeIndex(index) {
        var _a;
        const previousIndex = this._activeIndex;
        this._activeIndex = index;
        const tab = (_a = this.tabs) === null || _a === void 0 ? void 0 : _a.get(index);
        if (!tab) {
            return;
        }
        if (tab.disabled) {
            this._activeIndex = previousIndex;
            return;
        }
        this.setActiveTab(tab, index);
    }
    getTabAlignment() {
        return `justify-content: ${this.tabAlignment}`;
    }
    ngAfterContentInit() {
        var _a;
        if (!((_a = this.tabs) === null || _a === void 0 ? void 0 : _a.length)) {
            return;
        }
        // The activeIndex input may not be a valid tab so reset to first tab in that case
        if (this._activeIndex > this.tabs.length) {
            this.activeIndex = 0;
        }
        const initialTab = this.tabs.get(this._activeIndex);
        // no tab preselected, _activeIndex == -1
        if (!initialTab) {
            return;
        }
        this.activeTabContent = initialTab.content;
    }
    setActiveTab(tab, index) {
        let canChangeTab = this.beforeTabChange(this.activeIndex, index);
        if (!isObservable(canChangeTab)) {
            canChangeTab = of(canChangeTab);
        }
        canChangeTab
            .pipe(take(1), filter((canChange) => canChange && !tab.disabled))
            .subscribe(() => {
            this._activeIndex = index;
            this.activeTabContent = tab.content;
            this.activeIndexChange.emit(index);
        });
    }
}
TabGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TabGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TabGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: TabGroupComponent, selector: "ps-ui-tab-group", inputs: { activeIndex: "activeIndex", beforeTabChange: "beforeTabChange", tabAlignment: "tabAlignment" }, outputs: { activeIndexChange: "activeIndexChange" }, queries: [{ propertyName: "tabs", predicate: TabComponent }], exportAs: ["psUiTabGroup"], ngImport: i0, template: "<ul class=\"nav nav-tabs\" role=\"tablist\" [style]=\"getTabAlignment()\">\n  <li\n    class=\"nav-item\"\n    [class.active]=\"i === activeIndex\"\n    [class.disabled]=\"tab.disabled\"\n    [class]=\"tab.className\"\n    *ngFor=\"let tab of tabs; let i = index\"\n  >\n    <a class=\"nav-link\" (click)=\"setActiveTab(tab, i)\">\n      <i *ngIf=\"tab.icon\" class=\"text-secondary fa fa-{{ tab.icon }}\"></i>\n      {{ tab.label\n      }}<span class=\"tab-count\" *ngIf=\"tab.count\"\n        >({{ tab.count | number }})</span\n      ></a\n    >\n  </li>\n</ul>\n\n<div class=\"tab-content\">\n  <ng-container *ngTemplateOutlet=\"activeTabContent\"></ng-container>\n  <ng-content></ng-content>\n</div>\n", styles: [".nav-tabs{border:none}.nav-item{border:none;margin-right:40px}.nav-item a{color:#000;font-weight:600;font-size:13px;padding-bottom:8px!important;padding-left:0;padding-right:0;text-transform:uppercase;border:none;font-family:OpenSans-Semibold}.nav-item.active a,.nav-item.active a:hover,.nav-item.active a:focus,.nav-item a:hover,.nav-item a:focus{background:none!important;border:none;position:relative;color:#000}.nav-item.active a:after,.nav-item a:hover:after{border-bottom:3px solid #4a91c5;bottom:1px;content:\"\";height:3px;left:0;position:absolute;width:100%}.nav-item.disabled a{cursor:not-allowed;color:#ccc}.nav-item.disabled:hover a:after,.nav-item.disabled:focus a:after{border-bottom:0}.nav-item a span{font-weight:400;color:#757575}.nav-link{cursor:pointer}.tab-content{background-color:#fff;padding:30px 0}.tab-count{margin-left:3px;font-family:OpenSans}\n"], directives: [{ type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }], pipes: { "number": i1.DecimalPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TabGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-tab-group', exportAs: 'psUiTabGroup', template: "<ul class=\"nav nav-tabs\" role=\"tablist\" [style]=\"getTabAlignment()\">\n  <li\n    class=\"nav-item\"\n    [class.active]=\"i === activeIndex\"\n    [class.disabled]=\"tab.disabled\"\n    [class]=\"tab.className\"\n    *ngFor=\"let tab of tabs; let i = index\"\n  >\n    <a class=\"nav-link\" (click)=\"setActiveTab(tab, i)\">\n      <i *ngIf=\"tab.icon\" class=\"text-secondary fa fa-{{ tab.icon }}\"></i>\n      {{ tab.label\n      }}<span class=\"tab-count\" *ngIf=\"tab.count\"\n        >({{ tab.count | number }})</span\n      ></a\n    >\n  </li>\n</ul>\n\n<div class=\"tab-content\">\n  <ng-container *ngTemplateOutlet=\"activeTabContent\"></ng-container>\n  <ng-content></ng-content>\n</div>\n", styles: [".nav-tabs{border:none}.nav-item{border:none;margin-right:40px}.nav-item a{color:#000;font-weight:600;font-size:13px;padding-bottom:8px!important;padding-left:0;padding-right:0;text-transform:uppercase;border:none;font-family:OpenSans-Semibold}.nav-item.active a,.nav-item.active a:hover,.nav-item.active a:focus,.nav-item a:hover,.nav-item a:focus{background:none!important;border:none;position:relative;color:#000}.nav-item.active a:after,.nav-item a:hover:after{border-bottom:3px solid #4a91c5;bottom:1px;content:\"\";height:3px;left:0;position:absolute;width:100%}.nav-item.disabled a{cursor:not-allowed;color:#ccc}.nav-item.disabled:hover a:after,.nav-item.disabled:focus a:after{border-bottom:0}.nav-item a span{font-weight:400;color:#757575}.nav-link{cursor:pointer}.tab-content{background-color:#fff;padding:30px 0}.tab-count{margin-left:3px;font-family:OpenSans}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { activeIndex: [{
                type: Input
            }], activeIndexChange: [{
                type: Output
            }], beforeTabChange: [{
                type: Input
            }], tabAlignment: [{
                type: Input
            }], tabs: [{
                type: ContentChildren,
                args: [TabComponent]
            }] } });

class TabsModule {
}
TabsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TabsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TabsModule, declarations: [TabComponent, TabGroupComponent, TabContentDirective], imports: [CommonModule], exports: [TabComponent, TabGroupComponent, TabContentDirective] });
TabsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TabsModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TabsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [TabComponent, TabGroupComponent, TabContentDirective],
                    exports: [TabComponent, TabGroupComponent, TabContentDirective],
                    imports: [CommonModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TabComponent, TabContentDirective, TabGroupComponent, TabsModule };
